import { isCurrentShelemView, type ShelemMove, type ShelemView } from "@bing/engine-shelem";
import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";
import type { SeatPlayer } from "./seat-identity";
import type { LobbyPlayer } from "./online-connection";
import { SHELEM_END_REVEAL_MS } from "./shelem-beats";
import { handEndKeyOf } from "./shelem-moments";
import { ShelemTableOrUpdating } from "./shelem-old-server";
import { END_REVEAL_DELAY_MS } from "./use-end-reveal";
import { WinOverlay } from "./ShelemGame";
import { OnlineGame } from "./OnlineGame";

/** The lobby roster as the felt and the result card both want it. ONE mapping, so a chair can
 *  never be a named person on the table and a nameless stand-in on the end screen. */
function seatPlayers(
  players: readonly LobbyPlayer[] | undefined,
): readonly SeatPlayer[] | undefined {
  return players?.map((p) => ({
    seat: p.seat,
    isBot: p.isBot,
    // The server resolves the display name; feeding it through `nickname` is what makes a seat
    // read app-consistently via `displayName()`.
    nickname: p.name,
    avatarId: p.user?.avatarId,
    avatarPhotoId: p.user?.avatarPhotoId,
    isOfficial: p.user?.isOfficial,
  }));
}

/** Online شلم — thin wrapper over the shared `OnlineGame` shell. */
export function ShelemOnlineGame(): ReactNode {
  const profile = useSession((s) => s.profile);
  return (
    <OnlineGame<ShelemMove, ShelemView>
      gameId="shelem"
      room="shelem-online"
      title={fa.shelem.title}
      // the end card waits for the match's LAST collect AND its last hand-end card (P1 Task 7, the
      // operator's «always shown»): شلم's own beat through the shell's `endRevealMs` — solo waits the
      // same. Read off the LAST frame: a match the ROOM ends (a team gone — «opponents-left») leaves its
      // frame mid-hand, with no hand end to wait for, and reveals at the shell's one beat; one it ends
      // ON the hand-end frame waits for that hand's card (fix round 1 — never pasur's `gameOver` test)
      endRevealMs={(v) => (handEndKeyOf(v) !== "" ? SHELEM_END_REVEAL_MS : END_REVEAL_DELAY_MS)}
      // an OLD game-server's frame (the push puts the web first) draws the «updating» notice, never a
      // crash (`shelem-old-server.tsx`)
      renderTable={({ view, legal, send, leave, players, timer, onInviteSeat, seatNotices }) => (
        <ShelemTableOrUpdating
          seatNotices={seatNotices}
          view={view}
          legal={legal}
          onMove={send}
          onLeave={leave}
          self={profile}
          // the countdown in your strip's one slot, and the host's «+» on a bot chair (P1-2)
          timer={timer}
          onInviteSeat={onInviteSeat}
          // EVERY online table gets the chat toggle (OnlineGame mounts it iff `lobby?.roomId`, sent on
          // every lobby frame), so its footprint is kept from the FIRST frame — a resume's state frame
          // lands before its lobby frame, and a slot that waited for it would move «شلم» (W5)
          chat
          players={seatPlayers(players)}
        />
      )}
      // …and no end card off such a frame (the group review's I-3, as پاسور's): the notice stays alone
      renderWin={({
        result,
        view,
        again,
        leave,
        players,
        againDisabled,
        againHint,
        againHidden,
      }) =>
        !isCurrentShelemView(view) ? null : (
          <WinOverlay
            result={result}
            teamScores={view.teamScores}
            you={view.you}
            // شلم's card names TEAMS, so there is no «بازیکن N» here — but the four FACES on the
            // team rows were positional stand-ins for want of exactly this roster.
            self={profile}
            players={seatPlayers(players)}
            lastHand={view.lastHand}
            targetScore={view.targetScore}
            playedHands={view.playedHands}
            onAgain={again}
            againDisabled={againDisabled}
            againHint={againHint}
            againHidden={againHidden}
            onLeave={leave}
          />
        )
      }
    />
  );
}
